// "Partes" da navegação 2026 (contexto GESTÃO): um nível de agrupamento acima dos
// módulos, orientado à intenção do gestor. Este arquivo é a fonte única do mapa
// Módulo→Parte, da ordem fixa das Partes e da função pura de re-bucketing.
//
// Invariante crítico (AC-1): o re-bucketing opera sobre a saída JÁ FILTRADA de
// buildGestaoGroups() — não avalia permissão/flag. Só reagrupa e reordena o que
// já veio visível; nenhum módulo oculto pelo buildRoutes é exposto aqui.
import { ROUTE_KEYS } from '@constants/navigation-routes'
import { routeModuleKey } from '@modules/navegacao-v2/utils/visible-modules'

// Fallback: módulo visível cujo module key não está no mapa cai em "Outros".
export const PARTE_OUTROS = 'outros'

// Parte "Configurações": renderizada fixada no rodapé da sidebar de Gestão (fora da
// lista principal de Partes). Exportada para a GestaoSidebar separá-la sem magic string.
export const PARTE_CONFIGURACOES = 'configuracoes'

// Ordem FIXA das Partes (D9): Gestão → Operação → Ferramentas → AAWZ Intelligence
// → Configurações → Outros (SEMPRE por último). Ícones = tokens já existentes em
// src/components/icons/svg/ (renderizados via <icon-svg>) — nunca hex, nunca v-icon.
export const PARTES = [
  { key: 'gestao', label: 'Gestão', icon: 'presentation-chart' },
  { key: 'operacao', label: 'Operação', icon: 'route-square' },
  { key: 'ferramentas', label: 'Ferramentas', icon: 'grid-six' },
  { key: 'aawz-intelligence', label: 'AAWZ Intelligence', icon: 'ai-sparkle' },
  { key: PARTE_CONFIGURACOES, label: 'Configurações', icon: 'setting' },
  { key: PARTE_OUTROS, label: 'Outros', icon: 'category' },
]

// Mapa Módulo→Parte (spec §4.1), keyed por module key = routeModuleKey(routeName)
// (1º segmento antes do '.'). As keys foram confirmadas contra navigation-routes.js.
//
// `brokkr` fica FORA do mapa por decisão (D4): cai em "Outros" pelo fallback, e um
// teste trava isso — adicioná-lo ao mapa é regressão, não correção.
export const MODULE_TO_PARTE = {
  // Parte 1 — Gestão
  'dashboard-bi': 'gestao',
  bi: 'gestao',
  embed: 'gestao',
  analise_mercado: 'gestao',
  'fee-fixo': 'gestao',
  // As duas faces do BPO financeiro ficam na mesma Parte: `finia` é o operacional
  // (Rotina Financeira) e `finance` o analítico. Sem esta entrada o módulo cairia
  // em "Outros" pelo fallback — longe do par com que o gestor o usa.
  finia: 'gestao',
  finance: 'gestao',
  captable: 'gestao',
  valuation: 'gestao',
  comissoes: 'gestao',
  treasury: 'gestao',
  dataRoom: 'gestao',
  professionals: 'gestao',
  rh: 'gestao',
  // Parte 2 — Ferramentas
  boletador: 'ferramentas',
  opportunities: 'ferramentas',
  task: 'ferramentas',
  tasks: 'ferramentas',
  tarefas: 'ferramentas',
  events: 'ferramentas',
  dashboard: 'ferramentas',
  servicos: 'ferramentas',
  'repositorio-arquivos': 'ferramentas',
  'aawz-drive': 'ferramentas',
  'workflow-engine': 'ferramentas',
  // Parte 3 — Operação
  crm: 'operacao',
  compliance: 'operacao',
  // Financial Planning: o item chega com routeName 'fp' (raiz, resolvida por
  // redirect), então o module key é 'fp' tanto pela raiz quanto por 'fp.home'.
  fp: 'operacao',
  'billing-system': 'operacao',
  ws: 'operacao',
  clientes: 'operacao',
  simulador: 'operacao',
  carteira: 'operacao',
  'back-office': 'operacao',
  'assets-maturity': 'operacao',
  requisicoes: 'operacao',
  // Inerte: o módulo é retirado dos grupos pelo hoisting do AAWZ Hub antes do
  // bucketing, então a entrada nunca é exercida no fluxo normal. É por isso que a
  // sidebar precisa do guard de itens avulsos.
  solicitacoes: 'operacao',
  // Parte 4 — AAWZ Intelligence
  'aawz-intelligence': 'aawz-intelligence',
  // Inerte, como `solicitacoes`: a visão imersiva entra em `buildRoutes` com
  // `group: false` e ocupa o slot `home` em `adaptGestaoGroups`
  // (`escolherHome`) ANTES do bucketing, então esta entrada nunca é exercida.
  // Fica só para o mapa bater chave a chave com `ROUTE_KEYS`
  // (ORGANIZACAO-MODULOS.md).
  [ROUTE_KEYS.IA_IMERSIVO]: 'aawz-intelligence',
  commercial: 'aawz-intelligence',
  // Parte 5 — Configurações
  // A Jornada fica no RODAPÉ, junto de Configurações — não dentro do submenu dela.
  // Vive em `buildJornadaRoutes`, um grupo próprio adjacente ao de Configurações e sem
  // gate de gestor (que é o ponto: a spec-005 deu estado por profissional). As duas navs
  // ficam na mesma vizinhança conceitual, e é só este mapa que decide onde ela aparece
  // na nav 2026. Assim o bloco Configurações e seus 11 subitens ficam intocados.
  'central-config': PARTE_CONFIGURACOES,
  'settings-v2': PARTE_CONFIGURACOES,
  // Parte 6 — Outros (Educacional, Market News, Suporte, Workspace)
  produtos: PARTE_OUTROS,
  openMarketWindow: PARTE_OUTROS,
  suporte: PARTE_OUTROS,
  workspace: PARTE_OUTROS,
}

/**
 * Parte de um module key. module key ausente/desconhecido → "Outros" (fallback D4).
 * @param {string} moduleKey  routeModuleKey(routeName)
 * @returns {string} key da Parte
 */
export function parteOfModuleKey(moduleKey) {
  if (!moduleKey) return PARTE_OUTROS
  const parte = MODULE_TO_PARTE[moduleKey]
  return parte || PARTE_OUTROS
}

/**
 * Achata os items dos grupos e os indexa por Parte, com dedup global por module key
 * (D10: "Profissionais" 1×) — 1ª ocorrência vence. Items sem module key são mantidos
 * (não deduplicados). Helper puro; null-checks tradicionais (sem optional chaining).
 * Os acumuladores nascem DENTRO da função: em escopo de módulo, a segunda chamada
 * com a mesma entrada devolveria menos itens.
 * @param {Array<{title:string, items:Array}>} groups
 * @returns {Object} mapa parteKey → items[]
 */
function collectItemsByParte(groups) {
  const byParte = {}
  const seen = {}
  ;(groups || []).forEach((group) => {
    const items = (group && group.items) || []
    items.forEach((item) => {
      if (!item) return
      const moduleKey = routeModuleKey(item.routeName)
      if (moduleKey && seen[moduleKey]) return
      if (moduleKey) seen[moduleKey] = true
      const parteKey = parteOfModuleKey(moduleKey)
      if (!byParte[parteKey]) byParte[parteKey] = []
      byParte[parteKey].push(item)
    })
  })
  return byParte
}

/**
 * Re-agrupa os grupos de buildGestaoGroups() em Partes. Função PURA: mesma entrada,
 * mesma saída, sem estado entre chamadas e sem mutar o que recebe. Monta
 * [{ key, label, icon, items }] iterando PARTES na ORDEM FIXA e descarta Partes sem
 * itens. Cada Item é repassado POR REFERÊNCIA, sem cópia e sem reescrita.
 *
 * NÃO filtra visibilidade — só reordena o que já veio (AC-1).
 *
 * @param {Array<{title:string, items:Array}>} groups saída de buildGestaoGroups().groups
 * @returns {Array<{key:string, label:string, icon:string, items:Array}>}
 */
export function bucketModulesIntoPartes(groups) {
  const byParte = collectItemsByParte(groups)
  return PARTES.filter((parte) => {
    const items = byParte[parte.key]
    return items && items.length
  }).map((parte) => ({
    key: parte.key,
    label: parte.label,
    icon: parte.icon,
    items: byParte[parte.key],
  }))
}
